import type { SalesTransactionDiscountType } from '@/api/models';

/**
 * Decimals the value field shows when it formats on blur (for a percentage,
 * of the percentage shown). Display only: the dialog's value is the typed
 * text (`parseValueText`), never the field's rounded number. Well past the
 * two a discount may have, so an over-precise value still reads as typed.
 */
export const DISCOUNT_FIELD_DECIMALS = 10;

/** The value field's format per discount type. */
export const DISCOUNT_FIELD_FORMAT: Record<
    SalesTransactionDiscountType,
    Intl.NumberFormatOptions
> = {
    // The field holds a fraction (0.1 shows as 10%); the dialog stores the
    // percentage itself (10). No grouping keeps the typed text one number.
    percentage: {
        style: 'percent',
        maximumFractionDigits: DISCOUNT_FIELD_DECIMALS,
        useGrouping: false,
    },
    fixed: {
        style: 'currency',
        currency: 'GEL',
        currencyDisplay: 'narrowSymbol',
        maximumFractionDigits: DISCOUNT_FIELD_DECIMALS,
        useGrouping: false,
    },
};

/** The type switch's options, in order. */
export const DISCOUNT_TYPE_OPTIONS: {
    type: SalesTransactionDiscountType;
    labelKey: string;
}[] = [
    { type: 'percentage', labelKey: 'seller.discount.percentage' },
    { type: 'fixed', labelKey: 'seller.discount.fixed' },
];

/**
 * The number in what the seller has typed so far (`10`, `10%`, `₾5,5`), as a
 * percentage or GEL: whatever is not a digit or a decimal separator is
 * dropped and a comma reads as a point. `null` for nothing typed; `NaN` for
 * text with two separators, which the validator then refuses.
 */
export const parseValueText = (text: string): number | null => {
    const kept = text.replace(/[^\d.,]/g, '').replace(/,/g, '.');
    if (kept === '' || kept === '.') {
        return null;
    }
    return /^\d*\.?\d*$/.test(kept) ? Number(kept) : Number.NaN;
};

/** The stored value → the field's (a fraction for a percentage). */
export const toFieldValue = (
    type: SalesTransactionDiscountType,
    value: number | null,
): number | undefined => {
    if (value === null || Number.isNaN(value)) {
        return undefined;
    }
    return type === 'percentage' ? value / 100 : value;
};
